import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import TextField from '@mui/material/TextField'
import { projectInputSchema, type ProjectDto, type ProjectInput } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { qk } from '../../lib/queryClient'
import { ErrorAlert } from '../common/Feedback'

type FormValues = z.input<typeof projectInputSchema>

const EMPTY: FormValues = {
  developerName: '',
  name: '',
  hebrewName: '',
  gush: '',
  parcel: '',
  city: '',
  street: '',
}

type Props = {
  open: boolean
  project?: ProjectDto | null
  onClose: () => void
  onCreated?: (project: ProjectDto) => void
}

export function ProjectDialog({ open, project, onClose, onCreated }: Props) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(project)

  const form = useForm<FormValues, unknown, ProjectInput>({
    resolver: formResolver(projectInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    form.reset(
      project
        ? {
            developerName: project.developerName ?? '',
            name: project.name ?? '',
            hebrewName: project.hebrewName,
            gush: project.gush ?? '',
            parcel: project.parcel ?? '',
            city: project.city ?? '',
            street: project.street ?? '',
          }
        : EMPTY
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id])

  const save = useMutation({
    mutationFn: (values: ProjectInput) =>
      isEdit
        ? api.patch<ProjectDto>(`/projects/${project?.id}`, values)
        : api.post<ProjectDto>('/projects', values),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      if (project) void queryClient.invalidateQueries({ queryKey: qk.project(project.id) })
      if (!isEdit) onCreated?.(saved)
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{isEdit ? 'עריכת פרויקט' : 'פרויקט חדש'}</DialogTitle>

        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="שם בעברית"
                required
                autoFocus
                {...form.register('hebrewName')}
                error={Boolean(errors.hebrewName)}
                helperText={errors.hebrewName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="שם באנגלית" {...form.register('name')} />
            </Grid>

            <Grid size={12}>
              <TextField
                label="החברה היזמית"
                {...form.register('developerName')}
                error={Boolean(errors.developerName)}
                helperText={errors.developerName?.message}
              />
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField label="גוש" {...form.register('gush')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField label="חלקה" {...form.register('parcel')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="עיר" {...form.register('city')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="רחוב" {...form.register('street')} />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {save.isPending ? 'שומר…' : 'שמירה'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
